import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';
import { formatJSDate } from '~/utils/date';

export interface DatePickerProps {
  /** The selected date. Pass `undefined` to show the placeholder. */
  value?: Date | undefined;
  /** Called when the user picks a date. */
  onChange: (date: Date | undefined) => void;
  /** Placeholder text shown when no date is selected. Default: "dd/mm/yyyy" */
  placeholder?: string;
  /** Disables the picker. */
  disabled?: boolean;
  /** Earliest selectable date — days before this are greyed out and unclickable. */
  fromDate?: Date;
  /** Latest selectable date — days after this are greyed out and unclickable. */
  toDate?: Date;
  /** Extra classes on the trigger button. */
  className?: string;
  /** Side the calendar pops up on. Default `'bottom'`. Use `'top'` when the
   *  picker lives near the bottom of a parent popover, where the default
   *  would cover sibling actions (e.g. an Apply button). */
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * A date picker built from Calendar + Popover.
 * Uncontrolled by react-hook-form — accepts `value` and `onChange` directly.
 * Use `FormDatePicker` for form-integrated usage.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  disabled,
  fromDate,
  toDate,
  className,
  side = 'bottom',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  // Build disabled matchers from fromDate / toDate so the calendar greys out
  // and blocks selection outside the allowed range.
  const disabledMatchers = [
    ...(fromDate ? [{ before: fromDate }] : []),
    ...(toDate ? [{ after: toDate }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
          {value ? formatJSDate(value) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side={side}>
        <Calendar
          mode="single"
          selected={value}
          {...(value ? { defaultMonth: value } : {})}
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
          disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
          {...(fromDate ? { startMonth: fromDate } : {})}
          {...(toDate ? { endMonth: toDate } : {})}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
